import { DownOutlined } from '@ant-design/icons';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip from '@mui/material/Tooltip';
import { Avatar, Button, Col, Dropdown, Input, Menu, Row } from 'antd';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { formatDate } from 'src/helpers/millisecondsToFormattedDate';
import { useDeployProject } from 'src/reactQuery/projects/mutations/usePostDeployment';
import { useUpdateProject } from 'src/reactQuery/projects/mutations/useUpdateProject';
import { useProject } from 'src/reactQuery/projects/queries/useProject';
import { logOut } from '../../actions/LoginAction';
import { createProjectAction, setCurrentProject } from '../../actions/ProjectAction';
import { toggleCartDrawer } from '../../actions/cartAction';
import cartIcon from '../../asset/cart.png';
import notificationIcon from '../../asset/notification.png';
import { SESSION_ID } from '../../constant';
import { useProjects } from '../../reactQuery/projects/queries/useProjects';
import { setItem } from '../../util/localStorage';
import CButton from '../Button';
import Modal from '../Modal/Modal';
import OutlinedButton from '../OutlinedButton/OutlinedButton';
import { AutocompleteSelect } from '../Select';
import './index.css';

const useStyles = makeStyles(() => ({
  popover: {
    pointerEvents: 'none'
  },
  popoverContent: {
    pointerEvents: 'auto'
  }
}));

const CHeader = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  // eslint-disable-next-line no-unused-vars
  const classes = useStyles();
  const { data: projectData, refetch } = useProject();
  // eslint-disable-next-line no-unused-vars
  const projectMutation = useUpdateProject({});
  const mutation = useDeployProject({
    onSuccess: () => {
      setButtonText('Content is up to date ✅');
      projectMutation.mutate({
        ...projectData,
        metadata: {
          ...projectData.metadata,
          deployment: {
            ...projectData.metadata.deployment,
            lastDeploymentAt: new Date().getTime()
          }
        }
      });
      refetch();
    }
  });

  const [openNewProjectModal, setOpenNewProjectModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [buttonText, setButtonText] = useState('');
  const [newName, setNewName] = useState('');

  const items = [{ title: 'Deploy' }, { title: 'Docs' }, { title: 'API' }];

  const queryClient = useQueryClient({
    defaultOptions: {
      queries: {
        staleTime: Infinity
      }
    }
  });

  const onLogOut = () => {
    setItem(SESSION_ID, '');
    queryClient.clear();
    dispatch(logOut(), () => history.push('/'));
  };

  const userState = useSelector(({ login }) => {
    return login.user;
  });

  const selectedProject = useSelector(({ project }) => {
    return project.currentProject;
  });

  const goToAccount = () => {
    history.push('/account/information');
  };

  const getFirstCharacter = () => {
    return userState.name ? userState.name[0].toUpperCase() : '';
  };

  const isAuthenticated = useSelector(({ login }) => login.user);
  const isCartOpen = useSelector(({ cart }) => cart.isCartOpen);
  const { data } = useProjects();
  const currentProject = useSelector(({ project }) => project.currentProject);
  const projects = data || [];

  const projectSelect = (project) => {
    dispatch(setCurrentProject(project));
    queryClient.clear();
    history.push('/content-model');
  };

  const goToAllProjects = () => {
    history.push('/projects');
  };

  // modal

  const textInput = React.useRef();

  const createNew = (e) => {
    e.preventDefault();
    dispatch(
      createProjectAction(newName, () => {
        console.error('Project was created successfully');
      })
    );
    setOpenNewProjectModal(false);
    setNewName('');
  };

  const onModalCancel = () => {
    setOpenNewProjectModal(false);
    setNewName('');
    textInput.current.value = '';
  };

  const onDeployButtonClick = () => {
    mutation.mutate();
    setButtonText('Deploying Changes ⌛');
  };

  const displayOriginalDeployButtonText = useCallback(() => {
    if (
      projectData?.metadata?.deployment?.lastContentModifiedAt >
      projectData?.metadata?.deployment?.lastDeploymentAt
    ) {
      setButtonText('Publish content changes 🔄');
    } else {
      // eslint-disable-next-line no-unused-vars
      setButtonText('Content is up to date ✅');
    }
  }, [projectData]);

  useEffect(() => {
    if (currentProject) {
      displayOriginalDeployButtonText();
    }
  }, [currentProject, dispatch, projectData]);

  // eslint-disable-next-line no-unused-vars
  const MyComponent = forwardRef(function MyComponent(props, ref) {
    return (
      <CButton
        cref={ref}
        {...props}
        onClick={buttonText === 'Publish content changes 🔄' ? onDeployButtonClick : null}
        type={buttonText === 'Content is up to date ✅' ? 'greenDevBtn' : 'yellowDevBtn'}
        xstyle={{ margin: '0px 12px', zIndex: 1500 }}
      >
        {buttonText}
      </CButton>
    );
  });

  const DeploymentButton = () => {
    const deployment = projectData?.metadata?.deployment;
    const [yearMonthDayLastContentModifiedAt, timeLastContentModifiedAt] = formatDate(
      new Date(deployment?.lastContentModifiedAt)
    );
    const [yearMonthDayLastDeployedAt, timeLastDeployedAt] = formatDate(
      new Date(deployment?.lastDeploymentAt)
    );

    const title = (
      <div>
        <h3 style={{ color: 'white' }}>Content is automatically published every 15 minutes</h3>
        <p style={{ color: '#eee' }}>
          Project last deployed at: {yearMonthDayLastDeployedAt} at {timeLastDeployedAt}
        </p>
        <p style={{ color: '#eee' }}>
          Content last modified at: {yearMonthDayLastContentModifiedAt} at{' '}
          {timeLastContentModifiedAt}
        </p>
      </div>
    );
    return (
      <Tooltip arrow title={title}>
        <MyComponent />
      </Tooltip>
    );
  };

  const UserBlock = (props) => {
    return (
      <Row align="middle" justify="end" {...props}>
        <Dropdown overlay={menu}>
          <Button
            style={{
              alignItems: 'center',
              border: 'none',
              boxShadow: 'none',
              display: 'flex',
              height: '40px',
              justifyContent: 'space-evenly',
              padding: '0px',
              width: '60px'
            }}
          >
            <Avatar
              style={{
                backgroundColor: '#fde3cf',
                borderRadius: '50%',
                color: '#f56a00'
              }}
            >
              {getFirstCharacter()}
            </Avatar>

            <DownOutlined
              style={{
                alignItems: 'center',
                color: '#6E7191',
                display: 'flex',
                justifyContent: 'center'
              }}
            />
          </Button>
        </Dropdown>
      </Row>
    );
  };

  const ProjectBlock = () => {
    const sortProjectsAlphabetically = projects.sort((a, b) =>
      a.name?.toLowerCase().localeCompare(b.name?.toLowerCase())
    );

    return (
      <AutocompleteSelect
        getOptionLabel={(option) => option.name}
        id="autocomplete-ProjectBlock"
        onSelect={projectSelect}
        onViewAll={goToAllProjects}
        options={sortProjectsAlphabetically}
        placeholderValue={currentProject?.name || 'Select Project'}
        styles={{
          width: '175px'
        }}
      />
    );
  };

  const menu = (
    <Menu>
      <Menu.Item key="account" onClick={goToAccount}>
        Account
      </Menu.Item>
      <Menu.Item key="log out" danger onClick={onLogOut}>
        Log out
      </Menu.Item>
    </Menu>
  );

  return (
    <>
      <div
        style={{
          left: '116px',
          position: 'absolute',
          transform: 'translateX(-50%)'
        }}
      >
        <div onClick={() => (selectedProject ? history.push('/') : null)}>
          <strong
            style={{
              color: '#5564F8',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 700,
              lineHeight: '27px'
            }}
          >
            MDELLO
          </strong>
        </div>
      </div>

      <div
        style={{
          backgroundColor: '#F0F4FF',
          height: '64px',
          left: '231px',
          position: 'absolute',
          top: '0px',
          width: '1px'
        }}
      />

      <div
        style={{
          left: '265px',
          position: 'absolute',
          top: '11px'
        }}
      >
        {isAuthenticated && currentProject ? <ProjectBlock /> : null}
      </div>

      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'row',
          position: 'absolute',
          right: '0px',
          top: '0px',
          width: 'fit-content',
          zIndex: '1500'
        }}
      >
        {items.map(({ title }, index) => {
          const renderItems = () => {
            switch (title) {
              case 'Docs':
                return (
                  <div
                    key={index}
                    style={{
                      color: '#9BA0AD',
                      margin: '0px 12px'
                    }}
                  >
                    <a
                      className="Docs_Link"
                      href="https://app.mdello.com/docs#/"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'none' }}
                      target="_blank"
                    >
                      {title}
                    </a>
                  </div>
                );
              case 'Deploy':
                if (projectData?.metadata?.deployment?.needsContentRebuild && currentProject) {
                  return <DeploymentButton />;
                }
                break;
              default:
                return (
                  <div
                    key={index}
                    style={{
                      color: '#9BA0AD',
                      margin: '0px 12px'
                    }}
                  >
                    {title}
                  </div>
                );
            }
          };
          return renderItems();
        })}
        <div>
          <OutlinedButton
            icon={cartIcon}
            isSelected={isCartOpen}
            onClick={() => {
              dispatch(toggleCartDrawer());
            }}
          />
        </div>
        <div>
          <OutlinedButton icon={notificationIcon} />
        </div>
        {isAuthenticated && (
          <div>
            <UserBlock style={{ paddingRight: '30px' }} />
          </div>
        )}
        {!isAuthenticated && <Col>Login</Col>}
        {!isAuthenticated && <Col>Register</Col>}
      </div>

      <div>
        <Modal
          handleClose={onModalCancel}
          isModalOpen={openNewProjectModal}
          styles={{
            '& .MuiDialogContent-root': {
              height: '120px'
            },
            width: '560px'
          }}
          titleContent={<h4 className="modal-title">New Project</h4>}
        >
          <form id="new-project-form" onSubmit={(e) => createNew(e)}>
            <Input
              ref={textInput}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Name"
              size="large"
              style={{ maxWidth: '80%' }}
            />
            <Button
              htmlType="submit"
              size="large"
              style={{ backgroundColor: '#3cc782', marginLeft: '10px' }}
              type="primary"
            >
              Create
            </Button>
          </form>
        </Modal>
      </div>
    </>
  );
};

export default CHeader;
